import { useEffect, useState } from "react";
import axios from "axios";
import { useApp } from "../context/AppContext";
import { getContent, API } from "../lib/api";
import { MapPin, Building2, Search, ExternalLink, Compass, LocateFixed, AlertCircle, Loader2, Plus, Check, X } from "lucide-react";

function haversine(a, b) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const x = Math.sin(dLat/2) ** 2 + Math.sin(dLon/2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(x));
}

export default function Mosques() {
  const { t } = useApp();
  const [places, setPlaces] = useState(null);
  const [coords, setCoords] = useState(null);
  const [originLabel, setOriginLabel] = useState(null);
  const [mosques, setMosques] = useState([]);
  const [radius, setRadius] = useState(5000);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [city, setCity] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestForm, setSuggestForm] = useState({ name: "", city: "", address: "", lat: "", lon: "", contributor: "", note: "" });
  const [suggestStatus, setSuggestStatus] = useState(null);

  useEffect(() => { getContent("holy_places").then(setPlaces); }, []);

  const fetchMosques = async (lat, lon, label) => {
    setLoading(true);
    setError(null);
    setMosques([]);
    setCoords({ lat, lon });
    if (label) setOriginLabel(label);
    try {
      const { data } = await axios.get(`${API}/mosques-nearby`, {
        params: { latitude: lat, longitude: lon, radius },
        timeout: 35000
      });
      const sorted = (data.results || [])
        .map(m => ({ ...m, distance_km: haversine({ lat, lon }, m) }))
        .sort((a, b) => a.distance_km - b.distance_km);
      setMosques(sorted);
      if (sorted.length === 0) setError("Aucune mosquée trouvée dans ce rayon. Essayez d'élargir la recherche.");
    } catch (e) {
      setError("Service de cartographie indisponible. Veuillez réessayer dans quelques instants.");
    } finally {
      setLoading(false);
    }
  };

  const locate = () => {
    if (!navigator.geolocation) {
      setError("Votre navigateur ne supporte pas la géolocalisation.");
      return;
    }
    setError(null);
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => fetchMosques(pos.coords.latitude, pos.coords.longitude, "Ma position"),
      (err) => {
        setLoading(false);
        if (err.code === 1) setError("Permission de localisation refusée. Utilisez la recherche par ville ci-dessous.");
        else if (err.code === 2) setError("Position indisponible. Vérifiez votre connexion.");
        else if (err.code === 3) setError("Délai dépassé. Réessayez ou recherchez par ville.");
        else setError("Impossible de récupérer la position.");
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 300000 }
    );
  };

  // Debounced geocoding
  useEffect(() => {
    if (city.trim().length < 3) { setSuggestions([]); return; }
    setSearching(true);
    const id = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/geocode`, { params: { q: city }, timeout: 12000 });
        setSuggestions(data || []);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 450);
    return () => clearTimeout(id);
  }, [city]);

  const pickSuggestion = (s) => {
    setCity(s.label);
    setSuggestions([]);
    fetchMosques(s.lat, s.lon, s.label);
  };

  return (
    <div className="space-y-6 fade-in" data-testid="mosques-page">
      <header className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-nour-primary text-white flex items-center justify-center"><Building2 className="w-6 h-6" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold">{t.mosques}</h1>
          <p className="text-sm text-nour-text2">{t.mosques_sub}</p>
        </div>
      </header>

      {/* Search & locate */}
      <section className="nour-card p-5 space-y-4" data-testid="mosques-finder">
        <div className="flex flex-wrap items-center gap-3">
          <button className="btn-primary" onClick={locate} disabled={loading} data-testid="mosques-locate">
            {loading
              ? <><Loader2 className="w-4 h-4 animate-spin"/> Recherche…</>
              : <><LocateFixed className="w-4 h-4"/> Me localiser</>}
          </button>

          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-nour-text2 whitespace-nowrap">Rayon</span>
            <input
              type="range" min="500" max="15000" step="500"
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              data-testid="mosques-radius"
              className="accent-[#0F6E56]"
            />
            <span className="text-xs font-semibold tabular-nums w-12 text-right">{(radius/1000).toFixed(1)} km</span>
          </div>
        </div>

        {/* City search */}
        <div className="relative">
          <div className="flex items-center gap-2 bg-emerald-50 border border-[rgba(29,158,117,0.25)] rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-nour-text2 shrink-0" />
            <input
              data-testid="mosques-city-input"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Ou cherchez par ville ou adresse (ex : Paris, Casablanca, Istanbul…)"
              className="flex-1 bg-transparent outline-none text-sm py-1"
            />
            {searching && <Loader2 className="w-4 h-4 animate-spin text-nour-text2"/>}
          </div>
          {suggestions.length > 0 && (
            <div className="absolute z-20 mt-1 left-0 right-0 nour-card p-1 max-h-64 overflow-y-auto" data-testid="mosques-suggestions">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => pickSuggestion(s)}
                  data-testid={`mosques-suggestion-${i}`}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-emerald-50 text-sm flex items-start gap-2"
                >
                  <MapPin className="w-3.5 h-3.5 text-nour-primary mt-0.5 shrink-0"/>
                  <span className="line-clamp-2">{s.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="mosques-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0"/>
            <span>{error}</span>
          </div>
        )}

        {originLabel && coords && (
          <div className="text-xs text-nour-text2 inline-flex items-center gap-1">
            <MapPin className="w-3 h-3"/> {originLabel} — {coords.lat.toFixed(3)}, {coords.lon.toFixed(3)}
          </div>
        )}
      </section>

      {/* Results */}
      {loading && (
        <div className="space-y-2" data-testid="mosques-loading">
          {[0, 1, 2].map(i => (
            <div key={i} className="nour-card p-4 h-16 animate-pulse bg-gradient-to-r from-emerald-50 to-emerald-100/40" />
          ))}
        </div>
      )}

      {!loading && mosques.length > 0 && (
        <section className="space-y-2" data-testid="mosques-results">
          <div className="text-xs uppercase tracking-wider text-nour-text2">{mosques.length} mosquée{mosques.length > 1 ? "s" : ""} trouvée{mosques.length > 1 ? "s" : ""}</div>
          {mosques.map(m => (
            <article key={m.id} className="nour-card p-3 flex items-center gap-3" data-testid={`mosque-${m.id}`}>
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                <Compass className="w-5 h-5 text-nour-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-semibold truncate">{m.name}</div>
                  {m.source === "curated" && <span className="chip chip-gold text-[10px] py-0 px-2" title="Mosquée vérifiée par tadekir">✓</span>}
                </div>
                {m.name_ar && <div className="font-arabic-ui text-sm text-nour-primary" dir="rtl">{m.name_ar}</div>}
                {m.addr && <div className="text-xs text-nour-text2 truncate">{m.addr}</div>}
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold tabular-nums">{m.distance_km.toFixed(2)} km</div>
                <a
                  className="text-xs text-nour-primary inline-flex items-center gap-1 hover:underline"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${m.lat},${m.lon}&travelmode=walking`}
                  target="_blank" rel="noreferrer"
                  data-testid={`mosque-directions-${m.id}`}
                >Itinéraire <ExternalLink className="w-3 h-3"/></a>
              </div>
            </article>
          ))}
        </section>
      )}

      {!loading && mosques.length === 0 && !error && (
        <div className="nour-card p-5 text-sm text-nour-text2" data-testid="mosques-empty">
          Appuyez sur <strong>Me localiser</strong> ou recherchez par ville pour découvrir les mosquées autour de vous. Données OpenStreetMap.
        </div>
      )}

      {/* Holy mosques */}
      <section data-testid="haramayn-section">
        <div className="flex items-center justify-between mb-3 mt-4">
          <h2 className="font-heading text-xl font-bold">Les trois mosquées sacrées</h2>
          <button
            className="btn-ghost"
            onClick={() => { setSuggestOpen(true); setSuggestStatus(null); }}
            data-testid="suggest-mosque-open"
          >
            <Plus className="w-4 h-4"/> Suggérer une mosquée
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {places?.haramayn?.map(h => (
            <article key={h.id} className="nour-card p-5 relative overflow-hidden" data-testid={`haram-${h.id}`}>
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-emerald-100/50 blur-xl" />
              <div className="relative">
                <div className="font-arabic-quran text-2xl text-nour-primary" dir="rtl">{h.name_ar}</div>
                <h3 className="font-heading font-bold mt-1">{h.name_fr}</h3>
                <p className="text-xs text-nour-text2 inline-flex items-center gap-1 mt-1"><MapPin className="w-3 h-3"/> {h.city}</p>
                <p className="text-sm mt-3 leading-relaxed">{h.description_fr}</p>
                <ul className="mt-3 space-y-1 text-xs text-nour-text2">
                  {h.highlights_fr.map((hl, i) => <li key={i}>• {hl}</li>)}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Hajj + Umrah */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="rites-section">
        <div className="nour-card p-5">
          <h2 className="font-heading text-lg font-bold mb-3">Guide du Hajj</h2>
          <ol className="space-y-3">
            {places?.hajj_steps?.map((s, i) => (
              <li key={i} className="flex gap-3" data-testid={`hajj-step-${i}`}>
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center font-bold text-nour-primary text-sm shrink-0">{i+1}</div>
                <div>
                  <div className="font-semibold text-sm">{s.day} · {s.name_fr}</div>
                  <p className="text-xs text-nour-text2 mt-0.5">{s.desc_fr}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className="nour-card p-5">
          <h2 className="font-heading text-lg font-bold mb-3">Guide de la Umrah</h2>
          <ol className="space-y-3">
            {places?.umrah_steps?.map((s) => (
              <li key={s.step} className="flex gap-3" data-testid={`umrah-step-${s.step}`}>
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center font-bold text-nour-primary text-sm shrink-0">{s.step}</div>
                <div>
                  <div className="font-semibold text-sm">{s.name_fr}</div>
                  <p className="text-xs text-nour-text2 mt-0.5">{s.desc_fr}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Suggest Modal */}
      {suggestOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSuggestOpen(false)} data-testid="suggest-modal">
          <div className="nour-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-heading text-lg font-bold">Suggérer une mosquée</h3>
                <p className="text-xs text-nour-text2">Aidez la communauté tadekir à enrichir la carte. Votre suggestion sera vérifiée.</p>
              </div>
              <button onClick={() => setSuggestOpen(false)} data-testid="suggest-close"><X className="w-5 h-5"/></button>
            </div>

            {suggestStatus === "success" ? (
              <div className="text-center py-6" data-testid="suggest-success">
                <Check className="w-10 h-10 text-emerald-600 mx-auto mb-2"/>
                <div className="font-semibold">Merci pour votre contribution !</div>
                <p className="text-sm text-nour-text2 mt-1">Votre suggestion sera examinée par l&apos;équipe tadekir.</p>
                <button className="btn-primary mt-4" onClick={() => setSuggestOpen(false)}>Fermer</button>
              </div>
            ) : (
              <form
                className="space-y-3"
                onSubmit={async (e) => {
                  e.preventDefault();
                  setSuggestStatus("submitting");
                  try {
                    await axios.post(`${API}/mosques/suggest`, {
                      name: suggestForm.name,
                      city: suggestForm.city,
                      address: suggestForm.address || null,
                      lat: parseFloat(suggestForm.lat),
                      lon: parseFloat(suggestForm.lon),
                      contributor: suggestForm.contributor || null,
                      note: suggestForm.note || null
                    });
                    setSuggestStatus("success");
                    setSuggestForm({ name: "", city: "", address: "", lat: "", lon: "", contributor: "", note: "" });
                  } catch (err) {
                    setSuggestStatus("error");
                  }
                }}
              >
                <label className="block">
                  <span className="text-xs text-nour-text2">Nom de la mosquée *</span>
                  <input required value={suggestForm.name} onChange={(e) => setSuggestForm(f => ({ ...f, name: e.target.value }))} data-testid="suggest-name" className="mt-1 w-full bg-emerald-50 border border-[rgba(29,158,117,0.25)] rounded-lg px-3 py-2 outline-none"/>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs text-nour-text2">Ville *</span>
                    <input required value={suggestForm.city} onChange={(e) => setSuggestForm(f => ({ ...f, city: e.target.value }))} data-testid="suggest-city" className="mt-1 w-full bg-emerald-50 border border-[rgba(29,158,117,0.25)] rounded-lg px-3 py-2 outline-none"/>
                  </label>
                  <label className="block">
                    <span className="text-xs text-nour-text2">Adresse</span>
                    <input value={suggestForm.address} onChange={(e) => setSuggestForm(f => ({ ...f, address: e.target.value }))} data-testid="suggest-address" className="mt-1 w-full bg-emerald-50 border border-[rgba(29,158,117,0.25)] rounded-lg px-3 py-2 outline-none"/>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs text-nour-text2">Latitude *</span>
                    <input required type="number" step="any" value={suggestForm.lat} onChange={(e) => setSuggestForm(f => ({ ...f, lat: e.target.value }))} data-testid="suggest-lat" className="mt-1 w-full bg-emerald-50 border border-[rgba(29,158,117,0.25)] rounded-lg px-3 py-2 outline-none"/>
                  </label>
                  <label className="block">
                    <span className="text-xs text-nour-text2">Longitude *</span>
                    <input required type="number" step="any" value={suggestForm.lon} onChange={(e) => setSuggestForm(f => ({ ...f, lon: e.target.value }))} data-testid="suggest-lon" className="mt-1 w-full bg-emerald-50 border border-[rgba(29,158,117,0.25)] rounded-lg px-3 py-2 outline-none"/>
                  </label>
                </div>
                <p className="text-[10px] text-nour-text2">💡 Astuce : ouvrez Google Maps, clic droit sur la mosquée → copiez les coordonnées (lat, lon).</p>
                <label className="block">
                  <span className="text-xs text-nour-text2">Votre prénom (optionnel)</span>
                  <input value={suggestForm.contributor} onChange={(e) => setSuggestForm(f => ({ ...f, contributor: e.target.value }))} data-testid="suggest-contributor" className="mt-1 w-full bg-emerald-50 border border-[rgba(29,158,117,0.25)] rounded-lg px-3 py-2 outline-none"/>
                </label>
                <label className="block">
                  <span className="text-xs text-nour-text2">Note (optionnel)</span>
                  <textarea rows="2" value={suggestForm.note} onChange={(e) => setSuggestForm(f => ({ ...f, note: e.target.value }))} data-testid="suggest-note" className="mt-1 w-full bg-emerald-50 border border-[rgba(29,158,117,0.25)] rounded-lg px-3 py-2 outline-none resize-none"/>
                </label>
                {suggestStatus === "error" && <div className="text-sm text-red-700">Erreur lors de l&apos;envoi. Réessayez.</div>}
                <button className="btn-primary w-full justify-center" type="submit" disabled={suggestStatus === "submitting"} data-testid="suggest-submit">
                  {suggestStatus === "submitting" ? <Loader2 className="w-4 h-4 animate-spin"/> : "Envoyer la suggestion"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
